import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MetsIcon from 'commons/components/MetsIcon';
import CheckboxGroup from 'commons/components/CheckboxGroup';

import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { fetchRolesAndPermissions } from 'UserRegistration/resources/api/configurationData';
import { isSaveButtonDisabled } from 'UserRegistration/contexts/configs/componentConfig';
import { saveSection } from 'UserRegistration/resources/utils/saveUtils';
import RegistrationSection from 'UserRegistration/components/RegistrationSteps/RegistrationSection';

function BaseRolesAndPrivileges(props) {

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const [ roles, setRoles ] = useState([]);
    const [ privilegeGroups, setPrivilegeGroups ] = useState([]);
    const [ selectedRoles, setSelectedRoles ] = useState([]);
    const { t } = useTranslation(componentConfig.i18nNamespaces);

    useEffect(() => {

        fetchRolesAndPermissions(registrationData.registrationModel.subscription.selectedSubscriptionItemIds).then((data) => {
            setRoles(data.roles);
            setPrivilegeGroups(data.privilegeGroups);
        });

    }, []);

    const saveAndContinue = () => {
        saveSection(componentConfig, registrationData, setRegistrationData);
    };

    return (

        <RegistrationSection
            className="roles-and-privileges"
            id="rolesAndPrivileges"
            saveButtonDisabled={isSaveButtonDisabled(registrationData, componentConfig)}
            saveButtonOnClick={saveAndContinue}
            sectionNumber={props.sectionNumber}
            title={t('rolesPrivileges')}>

            <h3>{t('roles')}</h3>

            <div className="roles">

                <CheckboxGroup
                    checkboxIdPrefix="roleCheckbox"
                    items={roles.map((role) => {
                        return { label: role.resolvedName, value: role.id.toString() };
                    })}
                    layout="grid"
                    onClick={(e) => {
                        
                        const registrationDataCopy = Object.assign({}, registrationData);
                        const selectedRoleId = parseInt(e.target.value);
                        const selectedPrivileges = roles.filter((role) => {
                            return role.id === selectedRoleId;
                        }).map((role) => {
                            return role.associatedPrivileges.map((privilege) => {
                                return privilege.code;
                            });
                        })[0];

                        // If checkbox is now checked, add the selected role.
                        if (e.target.checked) {

                            setSelectedRoles([...selectedRoles, { roleId: selectedRoleId, privileges: selectedPrivileges }]);

                            registrationDataCopy.registrationModel.roleSelection.selectedRoleIds = [...registrationDataCopy.registrationModel.roleSelection.selectedRoleIds, selectedRoleId];

                        } else {

                            // If checkbox was unchecked, remove the selected role.

                            const selectedRolesCopy = [...selectedRoles];

                            setSelectedRoles(selectedRolesCopy.filter((selectedRole) => {
                                return selectedRoleId !== selectedRole.roleId;
                            }));

                            registrationDataCopy.registrationModel.roleSelection.selectedRoleIds = registrationDataCopy.registrationModel.roleSelection.selectedRoleIds.filter((roleId) => {
                                return roleId !== selectedRoleId;
                            });

                        }

                        setRegistrationData(registrationDataCopy);

                    }}
                />

            </div>

            <div id="associatedPrivileges" className="section-box">
                <h3>{t('associatedPrivileges')}</h3>
                <div className="privileges">

                    {privilegeGroups && privilegeGroups.map((privilegeGroup, privilegeGroupIndex) => {
                        return <div key={privilegeGroupIndex}>
                            <h4>{privilegeGroup.resolvedName}</h4>
                            <ul className="checkmarks">
                                {privilegeGroup.privileges.map((privilege, privilegeIndex) => {

                                    // Flag that indicates if the privilege is active based on currently selected roles.
                                    let isPrivilegeActive = false;

                                    selectedRoles.forEach((role) => {
                                        // If we find the privilege in any of the selected roles, set flag to true.
                                        if (role.privileges.includes(privilege.code)) {
                                            isPrivilegeActive = true;
                                        }
                                    });

                                    return <li className={isPrivilegeActive ? 'active' : 'inactive'} key={privilegeIndex}>
                                        {isPrivilegeActive && <i className="checkmark"></i>}
                                        {!isPrivilegeActive && <MetsIcon name="svg_xIcon" />}
                                        {privilege.resolvedName}
                                    </li>
                                })}
                            </ul>
                        </div>
                    })}

                </div>
            </div>

        </RegistrationSection>

    );

}

BaseRolesAndPrivileges.propTypes = {
    /** A key that corresponds to a property of the registrationModel object. @see UserRegistration.js */
    modelKey: PropTypes.string,
    /** The total number of configurable sections currently loaded in the instance of UserRegistration. */
    sectionCount: PropTypes.number,
    /** The position of this section in relation to other sections in the entire instance of UserRegistration. */
    sectionNumber: PropTypes.number
};

export default BaseRolesAndPrivileges;